<template>
  <div 
    v-if="show" 
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
    @click.self="handleCancel"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100">
      <!-- Header -->
      <div class="bg-blue-600 px-6 py-4 rounded-t-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-shield-alt text-white text-lg mr-3"></i>
            <h3 class="text-lg font-semibold text-white">Database Authentication</h3>
          </div>
          <button
            @click="handleCancel"
            class="text-blue-100 hover:text-white p-1 rounded"
            title="Cancel"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p class="text-blue-100 text-sm mt-2">
          Enter your password to access the database manager
        </p>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Error Message -->
        <div 
          v-if="error" 
          class="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span class="text-sm">{{ error }}</span>
        </div>

        <!-- Password Input -->
        <div class="mb-6">
          <label for="database-password" class="block text-sm font-medium text-gray-700 mb-2">
            Database Password
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fas fa-lock text-gray-400"></i>
            </div>
            <input
              id="database-password"
              ref="passwordInput"
              v-model="password"
              type="password"
              required
              class="appearance-none block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter your database password"
              @keyup.enter="handleSubmit"
              :disabled="loading"
            />
            
            <!-- Show/Hide Password Toggle -->
            <button
              type="button"
              @click="togglePasswordVisibility"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              :disabled="loading"
            >
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>

        <!-- Remember Password Option -->
        <div class="mb-6">
          <label class="flex items-center">
            <input
              v-model="rememberPassword"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              :disabled="loading"
            />
            <span class="ml-2 text-sm text-gray-700">
              Remember password for this session
            </span>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3">
          <button
            @click="handleCancel"
            :disabled="loading"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="handleSubmit"
            :disabled="loading || !password.trim()"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
            <span>{{ loading ? 'Verifying...' : 'Access Database' }}</span>
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="bg-gray-50 px-6 py-3 rounded-b-lg border-t border-gray-200">
        <div class="flex items-center text-xs text-gray-500">
          <i class="fas fa-info-circle mr-2"></i>
          <span>This password protects access to sensitive database operations</span>
        </div>
        
        <!-- Development/Testing Info -->
        <div class="mt-2 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded border-l-2 border-blue-400">
          <strong>For testing:</strong> Use passwords: admin123, database, or oddscomposer123
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['authenticated', 'cancel'])

// Reactive state
const password = ref('')
const showPassword = ref(false)
const rememberPassword = ref(false)
const loading = ref(false)
const error = ref('')
const passwordInput = ref(null)

// Focus password input when modal opens
watch(() => props.show, async (newValue) => {
  if (newValue) {
    // Reset form state
    password.value = ''
    error.value = ''
    loading.value = false
    
    // Focus input after modal is rendered
    await nextTick()
    if (passwordInput.value) {
      passwordInput.value.focus()
    }
  }
})

// Toggle password visibility
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
  const input = passwordInput.value
  if (input) {
    input.type = showPassword.value ? 'text' : 'password'
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!password.value.trim() || loading.value) return

  error.value = ''
  loading.value = true

  try {
    // Try the backend API first
    try {
      const response = await axios.post('/api/database/authenticate', {
        password: password.value
      })

      if (response.data.success) {
        // Store authentication in session if remember is checked
        if (rememberPassword.value) {
          sessionStorage.setItem('database_auth_token', response.data.token || 'authenticated')
          sessionStorage.setItem('database_auth_expires', Date.now() + (4 * 60 * 60 * 1000)) // 4 hours
        }

        emit('authenticated', {
          token: response.data.token || 'authenticated',
          remember: rememberPassword.value
        })
        return
      } else {
        error.value = response.data.message || 'Invalid password'
        return
      }
    } catch (apiError) {
      // If API is not available, fall back to simple password check
      console.warn('Database API not available, using fallback authentication:', apiError)
      
      // Simple fallback authentication - replace with your preferred password
      const validPasswords = ['admin123', 'database', 'oddscomposer123']
      
      if (validPasswords.includes(password.value)) {
        // Store authentication in session if remember is checked
        if (rememberPassword.value) {
          sessionStorage.setItem('database_auth_token', 'fallback_authenticated')
          sessionStorage.setItem('database_auth_expires', Date.now() + (4 * 60 * 60 * 1000)) // 4 hours
        }

        emit('authenticated', {
          token: 'fallback_authenticated',
          remember: rememberPassword.value
        })
        return
      } else {
        error.value = 'Invalid password. Please try again.'
        return
      }
    }
  } catch (err) {
    console.error('Database authentication error:', err)
    error.value = 'Authentication failed. Please check your connection and try again.'
  } finally {
    loading.value = false
  }
}

// Handle cancel/close
const handleCancel = () => {
  if (loading.value) return
  
  password.value = ''
  error.value = ''
  emit('cancel')
}

// Check if already authenticated in session
const checkExistingAuth = () => {
  const token = sessionStorage.getItem('database_auth_token')
  const expires = sessionStorage.getItem('database_auth_expires')
  
  if (token && expires && Date.now() < parseInt(expires)) {
    return token
  }
  
  // Clear expired auth
  sessionStorage.removeItem('database_auth_token')
  sessionStorage.removeItem('database_auth_expires')
  return null
}

// Expose method to parent component
defineExpose({
  checkExistingAuth
})
</script>

<style scoped>
/* Add subtle animations */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.transform {
  animation: modalSlideIn 0.3s ease-out;
}

/* Focus styles */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Disabled state */
input:disabled,
button:disabled {
  cursor: not-allowed;
}

/* Loading spinner */
.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style> 